<template>
    <BaseDialog :open="open" title="Klasse aus CSV importieren" size="lg" @close="closeModule">
        <p class="hint">
            Die Datei braucht die Spalten Vorname und Nachname. Die Kopfzeile und der Klassenname werden erkannt, wenn sie vorhanden sind; Exporte aus ScoolTools funktionieren direkt.
        </p>

        <label class="drop" :class="{ dragging, ready: csvData.length > 0, failed: error }"
            @dragover.prevent="dragging = true" @dragleave="dragging = false" @drop.prevent="onDrop">
            <span class="drop-icon"><component :is="csvData.length > 0 ? 'Check' : 'Upload'" aria-hidden="true" /></span>
            <span class="drop-text">
                <strong>{{ fileName || 'CSV-Datei hierher ziehen' }}</strong>
                <span>{{ dropHint }}</span>
            </span>
            <span class="btn">Datei wählen</span>
            <input ref="file" class="visually-hidden" type="file" accept=".csv,text/csv" @change="onFileChange" />
        </label>

        <template v-if="csvData.length > 0">
            <section class="preview-section">
                <h3 class="group-title">Vorschau: {{ previewLabel }}</h3>
                <ul class="student-list">
                    <li class="pill" v-for="(name, index) in importedNames" v-bind:key="index">{{ name }}</li>
                </ul>
            </section>

            <form class="field" @submit.prevent="importNames">
                <label class="field-label" for="import-class-name">Name der Klasse</label>
                <div class="inline-form">
                    <input id="import-class-name" class="input" v-model.trim="className" placeholder="z. B. 26Gx" maxlength="40">
                    <button type="submit" class="btn btn-primary" :disabled="className.length === 0 || importedNames.length === 0">Klasse importieren</button>
                </div>
            </form>
        </template>
    </BaseDialog>
</template>
<script>
import { Check, Upload } from '@lucide/vue';
import BaseDialog from './ui/BaseDialog.vue';
import { plural } from '../config.js';

export default {
    name: "ImportModule",
    data() {
        return {
            csvData: [],
            className: "",
            headersDetected: false,
            fileName: "",
            dragging: false,
            error: false,
        }
    },
    components: {
        BaseDialog, Check, Upload,
    },
    props: {
        open: Boolean,
    },
    emits: ['importNames', 'closeImportModule'],
    methods: {
        onFileChange(event) {
            if (!(event && event.target && event.target.files && event.target.files.length > 0)) return;
            this.detectHeaders(event.target.files[0]);
        },
        onDrop(event) {
            this.dragging = false;
            if (!(event.dataTransfer && event.dataTransfer.files.length > 0)) return;
            this.detectHeaders(event.dataTransfer.files[0]);
        },
        async detectHeaders(file) {
            if (!file) return;
            this.csvData = [];
            this.error = false;
            this.fileName = file.name;

            try {
                const text = await this.readFileAsText(file);
                const rawLines = text.split(/\r?\n/);
                const lines = rawLines.filter(line => line.trim().length > 0);
                // Try to auto-extract class name from early lines like: "Klasse 26Gx"
                if (this.className.length === 0 && lines.length > 0) {
                    const earlyLines = rawLines.slice(0, 10);
                    const delimiter = this.detectDelimiter(earlyLines.join('\n'));
                    for (const l of earlyLines) {
                        const firstCell = this.cells(l, delimiter)[0] || '';
                        const match = firstCell.match(/klasse\s+([A-Za-z0-9-_.]+)/i);
                        if (match && match[1]) {
                            this.className = match[1];
                            break;
                        }
                    }
                }

                // Try to find the header row and trim preamble rows
                const delimiterForHeader = this.detectDelimiter(rawLines.slice(0, 20).join('\n'));
                const headerIndex = rawLines.findIndex(l => {
                    const cells = this.cells(l, delimiterForHeader).map(c => c.toLowerCase());
                    return cells.includes('vorname') && cells.includes('nachname');
                });
                // Build our own data model ignoring Email
                if (headerIndex >= 0) {
                    const headerCells = this.cells(rawLines[headerIndex], delimiterForHeader);
                    const idxVor = headerCells.findIndex(h => h.toLowerCase() === 'vorname' || h.toLowerCase() === 'first' || h.toLowerCase() === 'firstname');
                    const idxNach = headerCells.findIndex(h => h.toLowerCase() === 'nachname' || h.toLowerCase() === 'last' || h.toLowerCase() === 'lastname' || h.toLowerCase() === 'surname');
                    const rows = [];
                    for (let i = headerIndex + 1; i < rawLines.length; i++) {
                        const line = rawLines[i];
                        if (!line || !line.trim()) continue;
                        const cells = this.cells(line, delimiterForHeader);
                        const vor = idxVor >= 0 ? (cells[idxVor] || '') : '';
                        const nach = idxNach >= 0 ? (cells[idxNach] || '') : '';
                        if (!vor && !nach) continue;
                        rows.push({ vorname: vor, nachname: nach });
                    }
                    this.csvData = rows;
                    this.headersDetected = true;
                } else {
                    // No header row found; take the first two columns, or the only one
                    const delimiter = this.detectDelimiter(rawLines.join('\n'));
                    const rows = lines
                        .map(l => this.cells(l, delimiter))
                        .filter(cells => cells[0] || cells[1])
                        .map(cells => ({ vorname: cells[0] || '', nachname: cells[1] || '' }));
                    this.csvData = rows;
                    this.headersDetected = false;
                }
                this.error = this.importedNames.length === 0;
            } catch (error) {
                console.error('Error detecting headers:', error);
                this.error = true;
            }
            if (this.error) this.csvData = [];
        },
        // One line as trimmed cells, without the quotes spreadsheets put around them
        cells(line, delimiter) {
            return line.split(delimiter).map(c => c.trim().replace(/^"(.*)"$/, '$1').trim());
        },
        detectDelimiter(sampleText) {
            const candidates = [',', ';', '\t'];
            let best = ',';
            let bestCount = -1;
            candidates.forEach(d => {
                const c = sampleText.split('\n').slice(0, 5).reduce((sum, line) => sum + (line.split(d).length - 1), 0);
                if (c > bestCount) { bestCount = c; best = d; }
            });
            return best;
        },
        // UTF-8 where the file is valid UTF-8, otherwise the Windows encoding Excel saves in, so umlauts survive.
        async readFileAsText(file) {
            const buffer = await file.arrayBuffer();
            let text;
            try {
                text = new TextDecoder('utf-8', { fatal: true }).decode(buffer);
            } catch (error) {
                text = new TextDecoder('windows-1252').decode(buffer);
            }
            return text.charCodeAt(0) === 0xFEFF ? text.slice(1) : text;
        },
        importNames() {
            if (this.importedNames.length === 0 || this.className.length === 0) {
                return;
            }

            this.$emit("importNames", {className: this.className, names: this.importedNames})

            // Reset everything after import
            this.resetImport();
        },
        resetImport() {
            this.csvData = [];
            this.className = "";
            this.headersDetected = false;
            this.fileName = "";
            this.error = false;
            if (this.$refs.file) this.$refs.file.value = "";
        },
        closeModule() {
            // Reset when closing
            this.resetImport();
            this.$emit("closeImportModule")
        },
    },
    computed: {
        importedNames() {
            if (!this.csvData || this.csvData.length === 0) return [];
            const headerKeywords = ['vorname','nachname','first','last','name','vor','nach','firstname','lastname'];
            return this.csvData
            .filter(row => {
                const a = (row.vorname || '').toString().trim();
                const b = (row.nachname || '').toString().trim();
                const combined = `${a} ${b}`.toLowerCase();
                if (!a && !b) return false;
                if (combined.includes('klasse')) return false;
                // filter out likely header rows appearing before/after
                if (headerKeywords.some(k => a.toLowerCase() === k || b.toLowerCase() === k)) return false;
                return true;
            })
            .map(student => {
                const firstName = (student.vorname || '').trim();
                const lastName = (student.nachname || '').trim();
                return `${firstName} ${lastName}`.trim();
            }).filter(name => name.length > 0);
        },
        previewLabel() {
            return plural(this.importedNames.length, 'Schüler:in', 'Schüler:innen')
        },
        dropHint() {
            if (this.error) return 'In dieser Datei habe ich keine Namen gefunden. Sie braucht eine Spalte Vorname und eine Spalte Nachname.'
            if (this.csvData.length > 0) return this.headersDetected ? 'Kopfzeile erkannt. Du kannst eine andere Datei wählen.' : 'Keine Kopfzeile gefunden: die ersten zwei Spalten gelten als Vor- und Nachname.'
            return 'oder eine Datei von deinem Computer wählen'
        }
    }
}
</script>

<style scoped>
.drop {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--s-4);
    padding: var(--s-5);
    border: 2px dashed var(--ink);
    border-radius: var(--r-leaf);
    background: var(--paper);
    cursor: pointer;
    transition: background-color var(--dur-fast) var(--ease), box-shadow var(--dur-fast) var(--ease);
}

.drop.dragging {
    border-style: solid;
    background: var(--hue-tint, var(--ground-tint));
    box-shadow: var(--shadow);
}

.drop.ready {
    border-style: solid;
}

.drop.failed {
    border-color: var(--danger);
    background: var(--danger-tint);
}

.drop:has(:focus-visible) {
    outline: 3px solid var(--ink);
    outline-offset: 2px;
}

.drop-icon {
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border: var(--line);
    border-radius: 50%;
    background: var(--hue);
}

.drop.ready .drop-icon {
    background: var(--ink);
    color: var(--paper);
}

.drop-icon svg {
    width: 22px;
    height: 22px;
    stroke-width: 2.5;
}

.drop-text {
    flex: 1 1 220px;
    display: grid;
    gap: 2px;
    min-width: 0;
    font-size: var(--t-sm);
    font-weight: 600;
    color: var(--ink-2);
}

.drop.failed .drop-text {
    color: var(--ink);
}

.drop-text strong {
    font-size: var(--t-md);
    font-weight: 800;
    color: var(--ink);
    overflow-wrap: anywhere;
}

.preview-section {
    display: grid;
    gap: var(--s-2);
}

.student-list {
    display: flex;
    flex-wrap: wrap;
    gap: var(--s-2);
    max-height: 220px;
    margin: 0;
    padding: var(--s-3);
    overflow-y: auto;
    list-style: none;
    border: var(--line);
    border-radius: var(--r-control);
    background: var(--hue-wash, var(--ground-wash));
}
</style>
